import React, { createContext, useState, useEffect, useContext } from 'react';

// Create the cart context
const CartContext = createContext();

// Custom hook to use the cart context
export const useCart = () => {
  return useContext(CartContext);
};

// Cart provider component
export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [cartCount, setCartCount] = useState(0);

  // Load cart from localStorage on initial render
  useEffect(() => {
    const storedCart = localStorage.getItem('shoppingCart');
    if (storedCart) {
      const parsedCart = JSON.parse(storedCart);
      setCartItems(parsedCart);
      updateCartCount(parsedCart);
    }
  }, []);

  // Update cart count whenever cartItems changes
  const updateCartCount = (items) => {
    const count = items.reduce((total, item) => total + (item.quantity || 1), 0);
    setCartCount(count);
  };

  // Add item to cart
  const addToCart = (item) => {
    // Check if item already exists in cart
    const existingItemIndex = cartItems.findIndex(
      cartItem => cartItem.id === item.id
    );

    let updatedCart;

    if (existingItemIndex >= 0) {
      // Item exists, update quantity
      updatedCart = [...cartItems];
      updatedCart[existingItemIndex].quantity = (updatedCart[existingItemIndex].quantity || 1) + 1;
    } else {
      // Check if all items in cart have the same discount percentage
      let commonDiscount = null;
      
      if (cartItems.length > 0) {
        // Get the discount of the first item
        commonDiscount = cartItems[0].discountPercentage;
        
        // Check if all items have the same discount
        const allSameDiscount = cartItems.every(cartItem => 
          cartItem.discountPercentage === commonDiscount
        );
        
        // If not all items have the same discount, or if the discount is undefined, don't apply any discount
        if (!allSameDiscount || commonDiscount === undefined) {
          commonDiscount = null;
        }
      }
      
      // Item doesn't exist, add new item with quantity 1
      const newItem = { ...item, quantity: 1 };
      
      // Apply the common discount if it exists
      if (commonDiscount !== null) {
        newItem.discountPercentage = commonDiscount;
        newItem.originalPrice = item.price; // Store original price
      }
      
      updatedCart = [...cartItems, newItem];
    }

    // Update state and localStorage
    setCartItems(updatedCart);
    localStorage.setItem('shoppingCart', JSON.stringify(updatedCart));
    updateCartCount(updatedCart);
    
    return updatedCart;
  };

  // Remove item from cart
  const removeFromCart = (itemId) => {
    const updatedCart = cartItems.filter(item => item.id !== itemId);
    setCartItems(updatedCart);
    localStorage.setItem('shoppingCart', JSON.stringify(updatedCart));
    updateCartCount(updatedCart);
    
    return updatedCart;
  };

  // Update item quantity
  const updateQuantity = (itemId, quantity) => {
    if (quantity < 1) return cartItems;

    const updatedCart = cartItems.map(item => 
      item.id === itemId ? { ...item, quantity } : item
    );
    
    setCartItems(updatedCart);
    localStorage.setItem('shoppingCart', JSON.stringify(updatedCart));
    updateCartCount(updatedCart);
    
    return updatedCart;
  };

  // Clear cart
  const clearCart = () => {
    setCartItems([]);
    localStorage.removeItem('shoppingCart');
    setCartCount(0);
  };

  // Apply discount to an item
  const applyDiscount = (itemId, discountPercentage) => {
    // Ensure discount is between 0-99
    const discount = Math.min(Math.max(0, discountPercentage), 99);
    
    const updatedCart = cartItems.map(item => {
      if (item.id === itemId) {
        return { 
          ...item, 
          discountPercentage: discount,
          originalPrice: item.originalPrice || item.price // Store original price if not already stored
        };
      }
      return item;
    });
    
    setCartItems(updatedCart);
    localStorage.setItem('shoppingCart', JSON.stringify(updatedCart));
    return updatedCart;
  };

  // Apply discount to multiple items
  const applyBulkDiscount = (discountPercentage) => {
    // Ensure discount is between 0-99
    const discount = Math.min(Math.max(0, discountPercentage), 99);
    
    const updatedCart = cartItems.map(item => {
      return { 
        ...item, 
        discountPercentage: discount,
        originalPrice: item.originalPrice || item.price // Store original price if not already stored
      };
    });
    
    setCartItems(updatedCart);
    localStorage.setItem('shoppingCart', JSON.stringify(updatedCart));
    return updatedCart;
  };

  // Value to be provided by the context
  const value = {
    cartItems,
    cartCount,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    applyDiscount,
    applyBulkDiscount
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext; 